import firebase from '../firebase'
import {useEffect} from 'react'

export const APP_VERSION = "3.1"
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000

// Best-effort device telemetry. Nothing in here may ever throw into the
// order screen: every write is wrapped and failures are only logged.
function currentPath () {
  try {
    return window.location.pathname + window.location.search
  } catch (e) {
    return null
  }
}

function safeEnv () {
  const env = {}
  try { env.platform = window.navigator.platform || null } catch (e) {}
  try { env.language = window.navigator.language || null } catch (e) {}
  try { env.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null } catch (e) {}
  return env
}

export default function useTracker (r_id, deviceKey) {

  useEffect(function trackDevice () {
    if (!deviceKey) {return}

    const deviceRef = firebase.database().ref(`devices`).child(deviceKey)
    const sessionsRef = deviceRef.child('sessions')
    const connectedRef = firebase.database().ref('.info/connected')

    // https://firebase.google.com/docs/firestore/solutions/presence
    async function onConnected (snapshot) {
      // If we're not currently connected, don't do anything.
      if (snapshot.val() === false) {
          return;
      }

      try {
        const path = currentPath()

        // Create a new session reference
        const sessionRef = sessionsRef.push()
        const sessionKey = sessionRef.key

        // FIRST: Create the session entry so the path exists
        await sessionRef.set({
          start: firebase.database.ServerValue.TIMESTAMP,
          end: null,
          r_id: r_id || null,
          path
        })

        // THEN: Set up onDisconnect handlers AFTER the data exists
        await deviceRef.onDisconnect().update({
          online: false,
          lastSeen: firebase.database.ServerValue.TIMESTAMP
        })
        await sessionRef.child('end').onDisconnect().set(
          firebase.database.ServerValue.TIMESTAMP
        )

        // Get current user uid if authenticated
        const currentUser = firebase.auth().currentUser
        const uid = currentUser ? currentUser.uid : null

        // Update device info
        const update = {
          online: true,
          currentSession: sessionKey,
          r_id: r_id || null,
          path,
          version: APP_VERSION,
          userAgent: window.navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          hostname: window.location.hostname,
          lastSeen: firebase.database.ServerValue.TIMESTAMP,
          uid,
          ...safeEnv()
        }
        await deviceRef.update(update)

        // firstSeen is written once, only if this record never had one
        const firstSeenSnap = await deviceRef.child('firstSeen').once('value')
        if (!firstSeenSnap.exists()) {
          await deviceRef.child('firstSeen').set(firebase.database.ServerValue.TIMESTAMP)
        }

        // Clean up sessions older than 2 days
        const twoDaysAgo = Date.now() - TWO_DAYS_MS
        const oldSessionsSnapshot = await sessionsRef
          .orderByChild('start')
          .endAt(twoDaysAgo)
          .once('value')

        if (oldSessionsSnapshot.exists()) {
          const updates = {}
          oldSessionsSnapshot.forEach((child) => {
            updates[child.key] = null
          })
          await sessionsRef.update(updates)
        }
      } catch (e) {
        console.log('tracker error', e)
      }
    }

    connectedRef.on('value', onConnected)
    return () => connectedRef.off('value', onConnected)
  },
  [r_id, deviceKey]) // only re-runs when this prop changes

}

// Cheap "still here" ping used when the route changes without a reconnect
// (admin navigating the SPA). Tablets never change routes.
export function touchDevice (deviceKey, r_id) {
  if (!deviceKey) return
  try {
    firebase.database().ref(`devices`).child(deviceKey).update({
      r_id: r_id || null,
      path: currentPath(),
      lastSeen: firebase.database.ServerValue.TIMESTAMP
    }).catch((e) => console.log('tracker touch error', e))
  } catch (e) {
    console.log('tracker touch error', e)
  }
}
